import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNil, toString } from 'lodash';
import {
  FUND_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE,
  FUND_INVESTMENTS_BY_MD_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetFundInvestmentsParams,
  GetFundInvestmentsResponse,
  GetFundInvestmentsReturn,
  UseGetFundInvestmentsParams,
} from './useGetFundInvestments.types';

const getFundInvestments = async (
  params: GetFundInvestmentsParams
): Promise<GetFundInvestmentsResponse | null> => {
  const { fundId, measurementDateSlug, measurementDate, checkForExport } = params;

  if (isNil(fundId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    // Pass null when slug is empty/undefined (for "Most Recent") to omit date parameter
    // Otherwise, use the date property (YYYY-MM-DD format) instead of slug
    const dateParam = isEmpty(measurementDateSlug) ? null : (measurementDate?.date ?? null);
    const fundInvestments = (await fundService.getFundInvestments(
      toString(fundId),
      dateParam,
      checkForExport
    )) as GetFundInvestmentsResponse;

    if (!fundInvestments) {
      return throwError(FUND_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return fundInvestments;
  } catch (error) {
    return throwError(FUND_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundInvestments = (params: UseGetFundInvestmentsParams): GetFundInvestmentsReturn => {
  const { fund, measurementDate, shouldQueryAutomatically = true, checkForExport = false } = params;

  const fundId = fund?.id;
  // Use slug to check if "Most Recent" (empty slug), but pass date property to endpoint
  const measurementDateSlug = measurementDate?.slug;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetFundInvestmentsReturn['data'],
    Error
  >({
    // Enable query when fundId exists and measurementDate is provided (slug can be empty for "Most Recent")
    enabled: !isNil(fundId) && measurementDate !== undefined && shouldQueryAutomatically,
    queryFn: () => getFundInvestments({ fundId, measurementDateSlug, measurementDate, checkForExport }),
    queryKey: [FUND_INVESTMENTS_BY_MD_QUERY_KEY, fundId, measurementDateSlug ?? 'most-recent', measurementDate?.date, checkForExport],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFundInvestments;
export { getFundInvestments };

